import { Button } from '@amore/ui/components/button'
import { Link, useNavigate } from '@tanstack/react-router'
import { ArrowRight, Plus, Wallet } from 'lucide-react'
import { replaceById, useCanEdit, useUpdate, useWedding } from '@/mock/store'
import { PromptList, taskPromptState, useAddPromptTask } from '@/patterns/prompts'
import { ScreenSection } from '@/patterns/screen'
import { useToast } from '@/shell/toaster'
import { byDueThenPriority } from '../tasks/model'
import { CompleteToggle, TaskTable, taskColumns } from '../tasks/task-table'
import { receptionGuests } from './model'

export function ReceptionPrompts() {
  const data = useWedding()
  const canEdit = useCanEdit()
  const update = useUpdate()
  const addTask = useAddPromptTask()
  const { showToast } = useToast()
  const guests = receptionGuests(data)
  const tasks = data.tasks.filter((task) => task.context?.type === 'reception')
  const candidates = [
    {
      code: 'reception-diets',
      title: 'Má každý host vhodnou variantu jídla?',
      hint: `Stravovací požadavky má ${guests.withDiet} hostů. Předejte je cateringu i cukrárně.`,
      taskTitle: 'Ověřit dietní varianty s dodavateli jídla',
      relevant: guests.withDiet > 0 && data.reception.offerings.length > 0,
    },
    {
      code: 'reception-vendor-meals',
      title: 'Počítáte i s jídlem pro dodavatele?',
      hint: 'Fotograf, kapela a další lidé s vámi budou celý den. Domluvte pro ně porce navíc.',
      taskTitle: 'Domluvit jídlo pro dodavatele',
      relevant: data.reception.offerings.length > 0 && data.reception.vendorMeals === null,
    },
  ]
  if (!canEdit) return null
  return (
    <PromptList
      title="Stojí za to ověřit"
      prompts={candidates.flatMap((prompt) => {
        const state = taskPromptState(tasks, prompt.taskTitle)
        if (!prompt.relevant || data.hiddenPrompts.includes(prompt.code) || state === 'done')
          return []
        return [
          {
            ...prompt,
            state,
            action: (
              <Button
                appearance="outline"
                tone="neutral"
                size="sm"
                onClick={() =>
                  addTask(prompt.taskTitle, { labels: [], context: { type: 'reception' } })
                }
              >
                <Plus className="size-3.5" aria-hidden="true" /> Přidat úkol
              </Button>
            ),
          },
        ]
      })}
      onHide={(code) => {
        update((current) => ({ ...current, hiddenPrompts: [...current.hiddenPrompts, code] }))
        showToast({
          title: 'Doporučení skryto',
          action: {
            label: 'Vrátit zpět',
            altText: 'Znovu zobrazit doporučení k jídlu a pití',
            onClick: () =>
              update((current) => ({
                ...current,
                hiddenPrompts: current.hiddenPrompts.filter((item) => item !== code),
              })),
          },
        })
      }}
    />
  )
}

export function ReceptionTasks() {
  const data = useWedding()
  const canEdit = useCanEdit()
  const update = useUpdate()
  const navigate = useNavigate()
  const tasks = data.tasks
    .filter((task) => task.context?.type === 'reception')
    .sort((left, right) => Number(left.done) - Number(right.done) || byDueThenPriority(left, right))
  return (
    <ScreenSection
      title="Co je potřeba zařídit"
      description="Úkoly k jídlu a pití jsou součástí vašeho společného svatebního plánu."
      action={
        <Link
          to="/tasks"
          className="inline-flex min-h-11 items-center gap-2 text-sm font-semibold text-action underline-offset-4 hover:underline focus-visible:outline-2 focus-visible:outline-ring"
        >
          Otevřít společné úkoly <ArrowRight className="size-3.5" aria-hidden="true" />
        </Link>
      }
    >
      <TaskTable
        tasks={tasks}
        grouped
        columns={taskColumns(data)}
        onOpen={(task) => void navigate({ to: `/tasks/${task.id}` })}
        rowAction={(task) => (
          <CompleteToggle
            task={task}
            canEdit={canEdit}
            onChange={(done) =>
              update((current) => ({
                ...current,
                tasks: replaceById(current.tasks, task.id, { done }),
              }))
            }
          />
        )}
        empty="Zatím žádné úkoly k jídlu a pití. Až nějaký přidáte, najdete ho tady i ve společných úkolech."
      />
    </ScreenSection>
  )
}

export function ReceptionBudgetNote() {
  return (
    <p className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-center text-xs text-muted-foreground">
      <Wallet aria-hidden="true" className="size-3.5 shrink-0" />
      Ceny, zálohy a platby za jídlo a pití máte v rozpočtu.
      <Link
        to="/budget"
        className="font-semibold text-action underline-offset-4 hover:underline focus-visible:outline-2 focus-visible:outline-ring"
      >
        Otevřít rozpočet
      </Link>
    </p>
  )
}
